import { Event } from 'Components/Common/models/Event';
import { api } from './api';
import { 
    CreateEventDto,
    EventDto,
    RecurrenceType,
    UpdateEvent,
    UpdateEventDto,
} from './dtos/event';
import { getEventOccurrenceKey } from 'utils/eventUtils';

const eventKindToApi = {
    Local: 0,
    Global: 1,
} as const;

const eventScopeToApi = {
    LocalOnly: 0,
    GlobalAllChildren: 1,
    GlobalSelectedChildren: 2,
} as const;

const serializeEventDto = <T extends CreateEventDto | UpdateEventDto>(data: T) => {
    const { howLong, ...dataWithoutHowLong } = data;
    const recurrenceType = data.recurrence?.type ?? RecurrenceType.None;
    const shouldIncludeHowLong = recurrenceType !== RecurrenceType.None
        && recurrenceType !== RecurrenceType.Yearly
        && howLong;

    return {
        ...dataWithoutHowLong,
        ...(shouldIncludeHowLong ? { howLong } : {}),
        kind: data.kind ? eventKindToApi[data.kind] : undefined,
        scope: data.scope ? eventScopeToApi[data.scope] : undefined,
    };
};

export const eventApi = api.injectEndpoints({
    endpoints: (builder) => ({
        getEvent: builder.query<EventDto, { entityId: string; id: string }>({
            query: ({ entityId, id }) => `enitity/${entityId}/event/${id}`,
            providesTags: (result, error, { id }) => [{ type: 'Event', id }],
        }),
        getEventsByEntity: builder.query<Event[], string | { entityId: string; rangeStart?: string; rangeEnd?: string }>({
            query: (arg) => {
                const entityId = typeof arg === 'string' ? arg : arg.entityId;
                const params = new URLSearchParams();

                if (typeof arg !== 'string') {
                    if (arg.rangeStart) {
                        params.set('rangeStart', arg.rangeStart);
                    }
                    if (arg.rangeEnd) {
                        params.set('rangeEnd', arg.rangeEnd);
                    }
                }

                const queryString = params.toString();
                return `entity/${entityId}/events${queryString ? `?${queryString}` : ''}`;
            },
            providesTags: (result, error, arg) => [
                { type: 'Event', id: `ENTITY_${typeof arg === 'string' ? arg : arg.entityId}` },
            ],
            transformResponse: (response: EventDto[]) =>
                response.map((eventDto) => ({
                    ...eventDto,
                    occurenceId: getEventOccurrenceKey(eventDto),
                })),
        }),
        createEvent: builder.mutation<EventDto, { entityId: string; data: CreateEventDto }>({
            query: ({ entityId, data }) => ({
                url: `entity/${entityId}/event/create`,
                method: 'POST',
                body: { dto: serializeEventDto(data) },
            }),
            invalidatesTags: (result, error, { entityId }) => [
                'Event',
                { type: 'Event', id: `ENTITY_${entityId}` },
            ],
            extraOptions: {
                successMessage: 'Wydarzenie zostało pomyślnie utworzone!',
                errorMessage: 'Nie udało się utworzyć wydarzenia.',
            },
        }),
        updateEvent: builder.mutation<void, { entityId: string; data: UpdateEventDto; mode: UpdateEvent }>({
            query: ({ entityId, data, mode }) => ({
                url: `entity/${entityId}/event/${data.id}/update?mode=${mode}`,
                method: 'PUT',
                body: { dto: serializeEventDto(data) },
            }),
            invalidatesTags: (result, error, { entityId, data }) => [
                'Event',
                { type: 'Event', id: data.id },
                { type: 'Event', id: `ENTITY_${entityId}` },
            ],
            extraOptions: {
                successMessage: 'Wydarzenie zostało pomyślnie zaktualizowane!',
                errorMessage: 'Nie udało się zaktualizować wydarzenia.',
            },
        }),
        deleteEvent: builder.mutation<void, { entityId: string; id: string; mode: UpdateEvent; occurrenceDate?: string }>({
            query: ({
                entityId,
                id,
                mode,
                occurrenceDate,
            }) => ({
                url: `entity/${entityId}/event/${id}/delete?mode=${mode}`,
                method: 'DELETE',
                body: {
                    mode,
                    occurrenceDate,
                },
            }),
            invalidatesTags: (result, error, { entityId, id }) => [
                'Event',
                { type: 'Event', id },
                { type: 'Event', id: `ENTITY_${entityId}` },
            ],
            extraOptions: {
                successMessage: 'Wydarzenie zostało pomyślnie usunięte!',
                errorMessage: 'Nie udało się usunąć wydarzenia.',
            },
        }),
        declineEvent: builder.mutation<void, { entityId: string; eventId: string; declineReason?: string; mode?: UpdateEvent; occurrenceDate?: string }>({
            query: ({ entityId, eventId, declineReason, mode, occurrenceDate }) => {
                const params = new URLSearchParams();
                if (mode !== undefined) params.set('mode', String(mode));
                if (occurrenceDate) params.set('occurrenceDate', occurrenceDate);

                return {
                    url: `entity/${entityId}/event/${eventId}/decline${params.toString() ? `?${params.toString()}` : ''}`,
                    method: 'POST',
                    body: { declineReason: declineReason ?? null },
                };
            },
            invalidatesTags: (result, error, { entityId, eventId }) => [
                'Event',
                { type: 'Event', id: eventId },
                { type: 'Event', id: `ENTITY_${entityId}` },
            ],
            extraOptions: {
                successMessage: 'Wypisano z wydarzenia.',
                errorMessage: 'Nie udało się wypisać z wydarzenia.',
            },
        }),
        selfAddToEvent: builder.mutation<void, { entityId: string; eventId: string; mode?: UpdateEvent; occurrenceDate?: string }>({
            query: ({ entityId, eventId, mode, occurrenceDate }) => {
                const params = new URLSearchParams();
                if (mode !== undefined) params.set('mode', String(mode));
                if (occurrenceDate) params.set('occurrenceDate', occurrenceDate);

                return {
                    url: `entity/${entityId}/event/${eventId}/self-add${params.toString() ? `?${params.toString()}` : ''}`,
                    method: 'POST',
                };
            },
            invalidatesTags: (result, error, { entityId, eventId }) => [
                'Event',
                { type: 'Event', id: eventId },
                { type: 'Event', id: `ENTITY_${entityId}` },
            ],
            extraOptions: {
                successMessage: 'Zapisano na wydarzenie.',
                errorMessage: 'Nie udało się zapisać na wydarzenie.',
            },
        }),
    }),
});

export const {
    useGetEventQuery,
    useGetEventsByEntityQuery,
    useCreateEventMutation,
    useUpdateEventMutation,
    useDeleteEventMutation,
    useDeclineEventMutation,
    useSelfAddToEventMutation,
} = eventApi;
